import { startSwapTransition } from '../../utils/swap-transition.js';
import { registerComponent } from '../../lib/bundle-registry.js';
import { VBElement } from '../../lib/vb-element.js';

let tabsVtId = 0;

class TabSet extends VBElement {
  #details = [];
  #summaries = [];
  #vtEnabled = false;
  #previousIndex = 0;

  setup() {
    this.#details = [...this.querySelectorAll(':scope > details')];
    this.#summaries = this.#details.map(d => d.querySelector('summary'));

    if (this.#details.length === 0) return false;

    // Explicit columns so the panel's grid-column: 1 / -1 spans every tab.
    // With only implicit columns, -1 is the end of column 1, so the first
    // tab stretched to the panel's width.
    this.style.setProperty('--_tab-count', String(this.#details.length));

    this.#setup();
    this.#ensureOneOpen();
    this.#initVT();
    return true;
  }

  teardown() {
    this.style.removeProperty('--_tab-count');
    this.#details = [];
    this.#summaries = [];
    this.#vtEnabled = false;
  }

  // ── Data API (HTML-first / JS-first dual contract) ──────────────

  get tabs() {
    return this.#details.map((d, i) => ({
      id: d.id || undefined,
      label: d.querySelector('summary')?.textContent?.trim() || `Tab ${i + 1}`,
      content: d.querySelector(':scope > :not(summary)')?.innerHTML || '',
      active: d.hasAttribute('open'),
    }));
  }

  /**
   * Replace the tab set and re-render. Each entry needs `label` (text)
   * and `content` (HTML or text). Optional `id` becomes the <details>'s
   * id; optional `active: true` opens that tab. Defaults to first tab
   * open if none specified.
   *
   * v1 is record-shaped — full rebuild on assignment.
   *
   * Emits tab-set:tabs-changed { tabs, source: 'property' }.
   */
  set tabs(value) {
    const next = Array.isArray(value) ? value : [];
    while (this.firstChild) this.firstChild.remove();
    let activeFound = false;
    for (const t of next) {
      const details = document.createElement('details');
      if (t.id) details.id = t.id;
      if (t.active && !activeFound) {
        details.setAttribute('open', '');
        activeFound = true;
      }
      const summary = document.createElement('summary');
      summary.textContent = t.label || '';
      details.appendChild(summary);
      const panel = document.createElement('div');
      panel.innerHTML = t.content || '';
      details.appendChild(panel);
      this.appendChild(details);
    }
    if (!activeFound && this.firstElementChild) {
      this.firstElementChild.setAttribute('open', '');
    }

    this._reinit();

    this.dispatchEvent(new CustomEvent('tab-set:tabs-changed', {
      detail: { tabs: next, source: 'property' },
      bubbles: true,
    }));
  }

  #initVT() {
    if (!this.hasAttribute('transition') || !document.startViewTransition) return;

    this.#vtEnabled = true;
    const id = ++tabsVtId;
    const type = this.getAttribute('transition') || 'fade';
    const vtClass = type === 'slide' ? 'vt-tabs-slide' : type === 'scale' ? 'vt-tabs-scale' : 'vt-tabs';

    this.#details.forEach((detail) => {
      const panel = detail.querySelector(':scope > :not(summary)');
      if (!panel) return;
      panel.style.viewTransitionName = `tabs-${id}`;
      panel.style.viewTransitionClass = vtClass;
    });

    this.#previousIndex = this.#details.findIndex(d => d.open);
    if (this.#previousIndex === -1) this.#previousIndex = 0;
  }

  #switchTab(newIndex) {
    if (newIndex === this.#previousIndex) return;

    const direction = newIndex > this.#previousIndex ? 'forward' : 'backward';
    document.documentElement.dataset.vtDirection = direction;

    const vt = startSwapTransition(() => {
      this.#details[newIndex].open = true;
    });

    vt.finished?.then(() => {
      delete document.documentElement.dataset.vtDirection;
    });
  }

  #setup() {
    this.#details.forEach((detail, i) => {
      const summary = this.#summaries[i];
      const panel = detail.querySelector(':scope > :not(summary)');

      if (!summary || !panel) return;

      // Generate IDs for programmatic relationships
      const tabId = summary.id || `tab-${crypto.randomUUID().slice(0, 8)}`;
      const panelId = panel.id || `panel-${crypto.randomUUID().slice(0, 8)}`;

      // Link summary ↔ panel (no ARIA role overrides — <summary> already
      // has implicit button role; <details name="..."> provides accessible
      // exclusive-accordion behavior natively)
      summary.id = tabId;
      summary.setAttribute('aria-controls', panelId);
      summary.setAttribute('tabindex', detail.open ? '0' : '-1');
      // <summary> exposes an implicit button role; aria-expanded is the
      // supported state attribute for it. aria-selected belongs to tab/
      // option roles, which this component deliberately does not apply.
      summary.setAttribute('aria-expanded', detail.open ? 'true' : 'false');

      panel.id = panelId;
      panel.setAttribute('aria-labelledby', tabId);

      this.listen(summary, 'click', (e) => {
        // Clicking (or Enter/Space on) the active tab is a no-op. Without
        // this, the native <summary> toggle closed it and left no panel.
        if (detail.open) {
          e.preventDefault();
          return;
        }
        // VT click interception — intercepts only when VT is active
        if (!this.#vtEnabled) return;
        e.preventDefault();
        this.#switchTab(i);
      });

      // Keyboard navigation
      this.listen(summary, 'keydown', (e) => this.#handleKey(e, i));

      // Sync ARIA state when details toggles
      this.listen(detail, 'toggle', () => this.#handleToggle(i));
    });
  }

  #ensureOneOpen() {
    // If none open, open the first one
    const hasOpen = this.#details.some(d => d.open);
    if (!hasOpen && this.#details.length > 0) {
      this.#details[0].open = true;
      this.#updateAriaStates();
    }
  }

  #handleToggle(index) {
    if (this.#details[index].open) {
      this.#previousIndex = index;
    }

    this.#updateAriaStates();

    this.dispatchEvent(new CustomEvent('tab-set:change', {
      detail: { index },
      bubbles: true
    }));
  }

  #updateAriaStates() {
    this.#details.forEach((detail, i) => {
      const summary = this.#summaries[i];
      summary.setAttribute('tabindex', detail.open ? '0' : '-1');
      summary.setAttribute('aria-expanded', detail.open ? 'true' : 'false');
    });
  }

  #handleKey(e, currentIndex) {
    const { key } = e;
    let newIndex;

    switch (key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % this.#details.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + this.#details.length) % this.#details.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.#details.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();

    if (this.#vtEnabled) {
      this.#switchTab(newIndex);
    } else {
      this.#details[newIndex].open = true;
    }
    this.#summaries[newIndex].focus();
  }
}

registerComponent('tab-set', TabSet);

export { TabSet };
